/**
 * InstrumentImportModal.tsx
 * App-context instrument catalog browser for importing instruments to user's library
 *
 * Based on onboarding InstrumentSelectionModal but adapted for app context:
 * - Checks against persisted instrument library (not draft state)
 * - Imports directly via instrumentService.importFromCatalog()
 * - Uses ImportConfirmationModal for duplicate handling (Option C Hybrid)
 * - "Create New" persists via InstrumentModal
 */

// 1. External dependencies
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, Package, CheckSquare } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Chip,
  useDisclosure,
  Listbox,
  ListboxItem,
  ListboxSection,
  Accordion,
  AccordionItem,
  useToastNotification,
} from '@enfomentis/ui';
import { InstrumentModal } from './InstrumentModal';
import { ImportConfirmationModal } from './ImportConfirmationModal';
import type { AssetClassType } from '@/types/shared/AssetClass';
import { ASSET_CLASS_TYPE_DISPLAY_MAP } from '@/constants/assetClass';
import { formatAssetClassTypeDisplay } from '@/utils/assetClass';
import type { Instrument } from '@/types/schema/Instrument';
import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';
import { INSTRUMENT_CATALOG, getAvailableMarketSegments } from '@/data/instrumentCatalog';
import { instrumentService } from '@/services/InstrumentService';

/**
 * Props for InstrumentImportModal
 */
export interface InstrumentImportModalProps {
  /** Whether the modal is open */
  isOpen: boolean;

  /** Handler for closing the modal */
  onClose: () => void;

  /** Called after successful import with IDs of newly created instruments */
  onImportComplete: (importedIds: string[]) => void;

  /** Selection mode: 'single' for one instrument, 'multiple' for batch import (default: 'multiple') */
  selectionMode?: 'single' | 'multiple';
}

/**
 * Filter state for instrument selection
 */
interface FilterState {
  searchTerm: string;
  assetClassFilter: AssetClassType | 'all';
  marketSegmentFilter: string;
  subAssetClassFilter: string;
}

/**
 * Asset class filter options
 */
const assetClassFilterOptions = [
  { key: 'all', label: 'All Asset Classes' },
  { key: 'futures' as AssetClassType, label: formatAssetClassTypeDisplay('futures', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'forex' as AssetClassType, label: formatAssetClassTypeDisplay('forex', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'stocks' as AssetClassType, label: formatAssetClassTypeDisplay('stocks', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'crypto' as AssetClassType, label: formatAssetClassTypeDisplay('crypto', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'options' as AssetClassType, label: formatAssetClassTypeDisplay('options', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'cfd' as AssetClassType, label: formatAssetClassTypeDisplay('cfd', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'bonds' as AssetClassType, label: formatAssetClassTypeDisplay('bonds', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'funds' as AssetClassType, label: formatAssetClassTypeDisplay('funds', ASSET_CLASS_TYPE_DISPLAY_MAP) },
] as const;

/**
 * Helper to get asset class display color
 */
const getAssetClassColor = (assetClass: AssetClassType): 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
  const colorMap: Record<AssetClassType, 'primary' | 'secondary' | 'success' | 'warning' | 'danger'> = {
    futures: 'primary',
    forex: 'success',
    stocks: 'secondary',
    crypto: 'warning',
    options: 'danger',
    cfd: 'primary',
    bonds: 'secondary',
    funds: 'success',
  };
  return colorMap[assetClass] || 'primary';
};

/**
 * InstrumentImportModal Component
 */
export const InstrumentImportModal: React.FC<InstrumentImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete,
  selectionMode = 'multiple',
}) => {
  // Catalog instruments (static data)
  const catalogInstruments = INSTRUMENT_CATALOG;

  // User's existing instruments (from database)
  const [userInstruments, setUserInstruments] = useState<Instrument[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Selection state
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: '',
    assetClassFilter: 'all',
    marketSegmentFilter: 'all',
    subAssetClassFilter: 'all',
  });

  // Confirmation modal state (for duplicates - Option C Hybrid)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedCatalogEntries, setSelectedCatalogEntries] = useState<InstrumentCatalogEntry[]>([]);

  // Instrument creation modal state
  const {
    isOpen: isInstrumentModalOpen,
    onOpen: onInstrumentModalOpen,
    onClose: onInstrumentModalClose,
  } = useDisclosure();

  // Toast notifications
  const { addToast } = useToastNotification();

  // Portal container ref for Select popovers (fixes aria-hidden warning)
  const portalContainerRef = useRef<HTMLDivElement>(null);

  // Compute set of catalog IDs already in user's library (active + inactive)
  const existingCatalogIds = useMemo(() => {
    const ids = new Set<string>();
    userInstruments.forEach((instrument) => {
      if (instrument.instrumentReferenceId) {
        ids.add(instrument.instrumentReferenceId);
      }
    });
    return ids;
  }, [userInstruments]);

  /**
   * Load user's instruments on mount and when modal opens
   */
  const loadUserInstruments = useCallback(async () => {
    try {
      setIsLoading(true);
      const instruments = await instrumentService.getAll();
      // Include active and inactive (exclude archived which are soft-deleted)
      const visibleInstruments = instruments.filter((i) => i.status !== 'archived');
      setUserInstruments(visibleInstruments);
    } catch (error) {
      console.error('Failed to load user instruments:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load user instruments when modal opens
  useEffect(() => {
    if (isOpen) {
      loadUserInstruments();
      // Reset filters and selection when modal opens
      setFilters({
        searchTerm: '',
        assetClassFilter: 'all',
        marketSegmentFilter: 'all',
        subAssetClassFilter: 'all',
      });
      setSelectedKeys(new Set());
    }
  }, [isOpen, loadUserInstruments]);

  // Filter catalog instruments based on current filter state
  const filteredInstruments = useMemo(() => {
    return catalogInstruments.filter((instrument) => {
      // Search term filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch =
          instrument.symbol.toLowerCase().includes(searchLower) ||
          instrument.name.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Asset class filter
      if (filters.assetClassFilter !== 'all' && instrument.classification.assetClass !== filters.assetClassFilter) {
        return false;
      }

      // Market segment filter
      if (
        filters.marketSegmentFilter !== 'all' &&
        instrument.classification.marketSegment !== filters.marketSegmentFilter
      ) {
        return false;
      }

      // Sub-asset class filter
      if (filters.subAssetClassFilter !== 'all') {
        if (!instrument.classification.subAssetClass && filters.subAssetClassFilter !== 'none') {
          return false;
        }
        if (
          instrument.classification.subAssetClass &&
          instrument.classification.subAssetClass !== filters.subAssetClassFilter
        ) {
          return false;
        }
      }

      return true;
    });
  }, [catalogInstruments, filters]);

  // Get unique market segments from catalog
  const availableMarketSegments = useMemo(() => {
    const segments = getAvailableMarketSegments();
    return [
      { key: 'all', label: 'All Market Segments' },
      ...segments.map((segment) => ({
        key: segment,
        label: segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' '),
      })),
    ];
  }, []);

  // Get unique sub-asset classes from filtered instruments
  const availableSubAssetClasses = useMemo(() => {
    const subAssetClasses = new Set<string>();

    // Only show sub-asset classes for the currently selected asset class and market segment
    const relevantInstruments = catalogInstruments.filter((instrument) => {
      if (filters.assetClassFilter !== 'all' && instrument.classification.assetClass !== filters.assetClassFilter) {
        return false;
      }
      if (
        filters.marketSegmentFilter !== 'all' &&
        instrument.classification.marketSegment !== filters.marketSegmentFilter
      ) {
        return false;
      }
      return true;
    });

    relevantInstruments.forEach((instrument) => {
      if (instrument.classification.subAssetClass) {
        subAssetClasses.add(instrument.classification.subAssetClass);
      }
    });

    return [
      { key: 'all', label: 'All Sub-Asset Classes' },
      { key: 'none', label: 'None (No Sub-Asset Class)' },
      ...Array.from(subAssetClasses)
        .sort()
        .map((subAssetClass) => ({
          key: subAssetClass,
          label: subAssetClass.charAt(0).toUpperCase() + subAssetClass.slice(1).replace('-', ' '),
        })),
    ];
  }, [catalogInstruments, filters.assetClassFilter, filters.marketSegmentFilter]);

  // Group instruments by asset class for sections
  const groupedInstruments = useMemo(() => {
    const groups = new Map<AssetClassType, InstrumentCatalogEntry[]>();

    filteredInstruments.forEach((instrument) => {
      const assetClass = instrument.classification.assetClass as AssetClassType;
      if (!groups.has(assetClass)) {
        groups.set(assetClass, []);
      }
      const group = groups.get(assetClass);
      if (group) {
        group.push(instrument);
      }
    });

    // Sort groups by asset class name and sort instruments within each group
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([assetClass, instruments]) => ({
        assetClass,
        instruments: instruments.sort((a, b) => a.symbol.localeCompare(b.symbol)),
      }));
  }, [filteredInstruments]);

  // Get selected instruments count
  const selectedCount = selectedKeys.size;

  // Handle select all/none
  const handleSelectAll = (): void => {
    const hasSelected = filteredInstruments.some((instrument) => selectedKeys.has(instrument.id));
    const filteredIds = new Set(filteredInstruments.map((instrument) => instrument.id));

    if (hasSelected) {
      // Deselect all filtered instruments
      setSelectedKeys((prev) => new Set([...prev].filter((key) => !filteredIds.has(key))));
    } else {
      // Select all filtered instruments
      setSelectedKeys((prev) => new Set([...prev, ...filteredIds]));
    }
  };

  // Update filter state
  const updateFilter = (field: keyof FilterState, value: string | AssetClassType): void => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Handle import action (Option C Hybrid)
   */
  const handleImport = useCallback(async (): Promise<void> => {
    if (selectedKeys.size === 0) return;

    // Determine keys to process (in single mode, use only first key if multiple somehow selected)
    let keysToProcess = selectedKeys;
    if (selectionMode === 'single' && selectedKeys.size > 1) {
      console.warn('InstrumentImportModal: single mode received multiple selections, using first only');
      const firstKey = Array.from(selectedKeys)[0];
      if (firstKey) {
        keysToProcess = new Set([firstKey]);
        setSelectedKeys(keysToProcess);
      }
    }

    // Get selected catalog entries
    const selected = catalogInstruments.filter((inst) => keysToProcess.has(inst.id));

    // Check if any selected instruments are already in library
    const hasDuplicates = selected.some((inst) => existingCatalogIds.has(inst.id));

    if (hasDuplicates) {
      // Open confirmation modal - let it handle the import
      setSelectedCatalogEntries(selected);
      setIsConfirmModalOpen(true);
    } else {
      // No duplicates - import directly
      try {
        const catalogIds = selected.map((inst) => inst.id);
        const result = await instrumentService.importFromCatalog(catalogIds);

        const importedIds = result.created.map((inst) => inst.id);

        if (importedIds.length > 0) {
          addToast({
            title: 'Import Successful',
            description: `${importedIds.length} instrument${importedIds.length === 1 ? '' : 's'} imported to your library`,
            color: 'success',
            timeout: 3000,
          });

          // Clear selection and close modal
          setSelectedKeys(new Set());
          onImportComplete(importedIds);
          onClose();
        } else if (result.errors.length > 0) {
          addToast({
            title: 'Import Failed',
            description: `${result.errors.length} error${result.errors.length === 1 ? '' : 's'} occurred during import`,
            color: 'danger',
            timeout: 5000,
          });
        }
      } catch (error) {
        console.error('Import failed:', error);
        addToast({
          title: 'Import Failed',
          description: 'An unexpected error occurred',
          color: 'danger',
          timeout: 5000,
        });
      }
    }
  }, [selectedKeys, catalogInstruments, existingCatalogIds, addToast, onImportComplete, onClose, selectionMode]);

  /**
   * Handle confirmation modal close (after import completes)
   */
  const handleConfirmModalComplete = useCallback((importedIds: string[]) => {
    // Close confirmation modal first
    setIsConfirmModalOpen(false);
    // Refresh user instruments to update existing badges
    loadUserInstruments();
    // Clear selection
    setSelectedKeys(new Set());
    // Notify parent with created IDs BEFORE closing (so dropdown can select while modal still mounted)
    onImportComplete(importedIds);
    // Close this modal
    onClose();
  }, [loadUserInstruments, onClose, onImportComplete]);

  /**
   * Handle cancel action
   */
  const handleCancel = (): void => {
    setSelectedKeys(new Set());
    setFilters({
      searchTerm: '',
      assetClassFilter: 'all',
      marketSegmentFilter: 'all',
      subAssetClassFilter: 'all',
    });
    onClose();
  };

  /**
   * Handle custom instrument creation
   */
  const handleCreateInstrument = (): void => {
    onInstrumentModalOpen();
  };

  /**
   * Handle saving new instrument from InstrumentModal
   */
  const handleInstrumentSave = useCallback(
    (instrument: Instrument): void => {
      // Refresh user instruments to include the new one
      loadUserInstruments();

      // Notify parent of the new instrument
      onImportComplete([instrument.id]);

      // Close both modals
      onInstrumentModalClose();
      onClose();

      addToast({
        title: 'Instrument Created',
        description: `${instrument.symbol} has been added to your library`,
        color: 'success',
        timeout: 3000,
      });
    },
    [loadUserInstruments, onImportComplete, onInstrumentModalClose, onClose, addToast]
  );

  /**
   * Handle instrument creation errors
   */
  const handleInstrumentError = (error: string): void => {
    console.error('Instrument creation error:', error);
    addToast({
      title: 'Creation Failed',
      description: error,
      color: 'danger',
      timeout: 5000,
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen && !isConfirmModalOpen && !isInstrumentModalOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        size="2xl"
        classNames={{
          base: 'max-h-[100vh] sm:max-h-[90vh] h-full sm:h-auto',
          wrapper: 'items-end sm:items-center p-0 sm:p-6',
          backdrop: 'bg-overlay/50',
        }}
        scrollBehavior="inside"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col gap-1 pb-0 px-4 sm:px-6 pt-4 sm:pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">Import from Catalog</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      color="secondary"
                      variant="bordered"
                      startContent={<Plus className="w-3 h-3" />}
                      onPress={handleCreateInstrument}
                      className="mr-4"
                    >
                      <span className="hidden sm:inline">Create New</span>
                      <span className="sm:hidden">New</span>
                    </Button>
                    {selectedCount > 0 && (
                      <Chip color="primary" size="sm" variant="flat">
                        {selectedCount} selected
                      </Chip>
                    )}
                  </div>
                </div>
                <p className="text-sm text-text-secondary font-normal">
                  Select instruments to add to your library
                </p>

                {/* Fixed Search and Filters Section */}
                {!isLoading && (
                  <div className="space-y-4 pt-4 border-t border-default-200" ref={portalContainerRef}>
                    <Input
                      placeholder="Search instruments by symbol or name..."
                      value={filters.searchTerm}
                      onValueChange={(value) => updateFilter('searchTerm', value)}
                      startContent={<Search className="w-4 h-4 text-text-secondary" />}
                      className="w-full"
                      classNames={{ input: 'text-base' }}
                    />

                    {/* Desktop: Show filters inline */}
                    <div className="hidden sm:block">
                      <div className="grid grid-cols-3 gap-4">
                        <Select
                          label="Asset Class"
                          variant="underlined"
                          size="sm"
                          selectedKeys={[filters.assetClassFilter]}
                          onSelectionChange={(keys) => {
                            updateFilter('assetClassFilter', Array.from(keys)[0] as string | AssetClassType);
                            updateFilter('marketSegmentFilter', 'all');
                            updateFilter('subAssetClassFilter', 'all');
                          }}
                          aria-label="Filter instruments by asset class"
                          {...(portalContainerRef.current && {
                            popoverProps: { portalContainer: portalContainerRef.current },
                          })}
                        >
                          {assetClassFilterOptions.map((item) => (
                            <SelectItem key={item.key}>{item.label}</SelectItem>
                          ))}
                        </Select>

                        <Select
                          label="Market Segment"
                          variant="underlined"
                          size="sm"
                          selectedKeys={[filters.marketSegmentFilter]}
                          onSelectionChange={(keys) => {
                            updateFilter('marketSegmentFilter', Array.from(keys)[0] as string);
                            updateFilter('subAssetClassFilter', 'all');
                          }}
                          aria-label="Filter instruments by market segment"
                          {...(portalContainerRef.current && {
                            popoverProps: { portalContainer: portalContainerRef.current },
                          })}
                        >
                          {availableMarketSegments.map((item) => (
                            <SelectItem key={item.key}>{item.label}</SelectItem>
                          ))}
                        </Select>

                        <Select
                          label="Sub-Asset Class"
                          variant="underlined"
                          size="sm"
                          selectedKeys={[filters.subAssetClassFilter]}
                          onSelectionChange={(keys) => updateFilter('subAssetClassFilter', Array.from(keys)[0] as string)}
                          aria-label="Filter instruments by sub-asset class"
                          {...(portalContainerRef.current && {
                            popoverProps: { portalContainer: portalContainerRef.current },
                          })}
                        >
                          {availableSubAssetClasses.map((item) => (
                            <SelectItem key={item.key}>{item.label}</SelectItem>
                          ))}
                        </Select>
                      </div>
                    </div>

                    {/* Mobile: Collapsible filters in Accordion */}
                    <div className="sm:hidden">
                      <Accordion variant="light" isCompact>
                        <AccordionItem
                          key="filters"
                          aria-label="Filter options"
                          title={`Filters ${filters.assetClassFilter !== 'all' || filters.marketSegmentFilter !== 'all' || filters.subAssetClassFilter !== 'all' ? '(Active)' : ''}`}
                          classNames={{
                            title: 'text-sm font-medium',
                            trigger: 'py-2',
                            content: 'pt-0 pb-2',
                          }}
                        >
                          <div className="space-y-3">
                            <Select
                              label="Asset Class"
                              variant="bordered"
                              size="sm"
                              selectedKeys={[filters.assetClassFilter]}
                              onSelectionChange={(keys) => {
                                updateFilter('assetClassFilter', Array.from(keys)[0] as string | AssetClassType);
                                updateFilter('marketSegmentFilter', 'all');
                                updateFilter('subAssetClassFilter', 'all');
                              }}
                              aria-label="Filter instruments by asset class"
                              {...(portalContainerRef.current && {
                                popoverProps: { portalContainer: portalContainerRef.current },
                              })}
                            >
                              {assetClassFilterOptions.map((item) => (
                                <SelectItem key={item.key}>{item.label}</SelectItem>
                              ))}
                            </Select>

                            <Select
                              label="Market Segment"
                              variant="bordered"
                              size="sm"
                              selectedKeys={[filters.marketSegmentFilter]}
                              onSelectionChange={(keys) => {
                                updateFilter('marketSegmentFilter', Array.from(keys)[0] as string);
                                updateFilter('subAssetClassFilter', 'all');
                              }}
                              aria-label="Filter instruments by market segment"
                              {...(portalContainerRef.current && {
                                popoverProps: { portalContainer: portalContainerRef.current },
                              })}
                            >
                              {availableMarketSegments.map((item) => (
                                <SelectItem key={item.key}>{item.label}</SelectItem>
                              ))}
                            </Select>

                            <Select
                              label="Sub-Asset Class"
                              variant="bordered"
                              size="sm"
                              selectedKeys={[filters.subAssetClassFilter]}
                              onSelectionChange={(keys) =>
                                updateFilter('subAssetClassFilter', Array.from(keys)[0] as string)
                              }
                              aria-label="Filter instruments by sub-asset class"
                              {...(portalContainerRef.current && {
                                popoverProps: { portalContainer: portalContainerRef.current },
                              })}
                            >
                              {availableSubAssetClasses.map((item) => (
                                <SelectItem key={item.key}>{item.label}</SelectItem>
                              ))}
                            </Select>
                          </div>
                        </AccordionItem>
                      </Accordion>
                    </div>

                    {/* Bulk Selection - only show in multiple mode */}
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-text-secondary">
                        {filteredInstruments.length} instrument{filteredInstruments.length !== 1 ? 's' : ''} found
                      </p>
                      {selectionMode === 'multiple' && (
                        <Button
                          size="sm"
                          variant="light"
                          onPress={handleSelectAll}
                          startContent={<CheckSquare className="w-3 h-3" />}
                        >
                          {filteredInstruments.some((instrument) => selectedKeys.has(instrument.id))
                            ? 'Deselect All'
                            : 'Select All'}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </ModalHeader>

              <ModalBody className="pt-4 px-4 sm:px-6 pb-4 sm:pb-6 flex-1 min-h-0">
                {isLoading ? (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-default-300 mx-auto mb-4 animate-pulse" />
                    <h4 className="text-large font-medium text-default-500 mb-2">Loading Instruments</h4>
                    <p className="text-small text-default-400">Please wait while we load the catalog...</p>
                  </div>
                ) : (
                  <div className="flex-1 min-h-0">
                    {groupedInstruments.length > 0 ? (
                      <Listbox
                        aria-label="Select instruments"
                        selectionMode={selectionMode}
                        selectedKeys={selectedKeys}
                        onSelectionChange={(keys) => {
                          setSelectedKeys(new Set(Array.from(keys) as string[]));
                        }}
                        className="w-full [&_[data-selected=true]]:!bg-transparent [&_[aria-selected=true]]:!bg-transparent [&_[data-selected=false]]:!bg-transparent [&_[aria-selected=false]]:!bg-transparent"
                      >
                        {groupedInstruments.map((group, groupIndex) => (
                          <ListboxSection
                            key={group.assetClass}
                            title={formatAssetClassTypeDisplay(group.assetClass, ASSET_CLASS_TYPE_DISPLAY_MAP)}
                            showDivider={groupIndex < groupedInstruments.length - 1}
                            classNames={{
                              heading: 'text-tiny font-semibold text-default-500 uppercase tracking-wide',
                              group: 'divide-y divide-default-100',
                            }}
                          >
                            {group.instruments.map((instrument) => {
                              const isInLibrary = existingCatalogIds.has(instrument.id);
                              return (
                                <ListboxItem
                                  key={instrument.id}
                                  className="py-3"
                                  textValue={`${instrument.symbol} ${instrument.name}`}
                                  startContent={
                                    <div
                                      className={`w-8 h-8 rounded-full bg-${getAssetClassColor(instrument.classification.assetClass as AssetClassType)} text-white flex items-center justify-center text-xs font-semibold`}
                                    >
                                      {instrument.symbol.substring(0, 2)}
                                    </div>
                                  }
                                >
                                  <div className="flex justify-between items-start w-full">
                                    {/* Left column: Symbol and description */}
                                    <div className="flex flex-col flex-1 min-w-0">
                                      <span className="text-small font-semibold">{instrument.symbol}</span>
                                      <span className="text-tiny text-default-400 truncate">{instrument.name}</span>
                                      {isInLibrary && (
                                        <span className="text-tiny text-warning-500 font-medium mt-1">
                                          Already in library
                                        </span>
                                      )}
                                    </div>

                                    {/* Right column: Market info */}
                                    <div className="flex flex-col items-end text-right ml-4 flex-shrink-0">
                                      <div className="text-tiny text-default-500 font-medium">
                                        <span className="capitalize">
                                          {(instrument.classification.marketSegment || '').replace('-', ' ')}
                                        </span>
                                      </div>
                                      {instrument.classification.subAssetClass && (
                                        <div className="text-tiny text-default-400">
                                          <span className="capitalize">
                                            {instrument.classification.subAssetClass.replace('-', ' ')}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </ListboxItem>
                              );
                            })}
                          </ListboxSection>
                        ))}
                      </Listbox>
                    ) : (
                      <div className="text-center py-8">
                        <Package className="w-12 h-12 text-default-300 mx-auto mb-4" />
                        <h4 className="text-large font-medium text-default-500 mb-2">No Instruments Found</h4>
                        <p className="text-small text-default-400">Try adjusting your search terms or filters</p>
                      </div>
                    )}
                  </div>
                )}
              </ModalBody>

              <ModalFooter className="px-4 sm:px-6 py-4 sm:py-6">
                <Button color="danger" variant="light" onPress={handleCancel}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onPress={handleImport}
                  isDisabled={selectedCount === 0}
                  startContent={<Plus className="w-4 h-4" />}
                >
                  {selectionMode === 'single'
                    ? 'Import Instrument'
                    : `Import ${selectedCount} Instrument${selectedCount !== 1 ? 's' : ''}`}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Confirmation modal for duplicates */}
      <ImportConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        selectedInstruments={selectedCatalogEntries}
        onImportComplete={handleConfirmModalComplete}
      />

      {/* Instrument creation modal */}
      <InstrumentModal
        isOpen={isInstrumentModalOpen}
        onClose={onInstrumentModalClose}
        onSave={handleInstrumentSave}
        onError={handleInstrumentError}
      />
    </>
  );
};

export default InstrumentImportModal;
